import { useEffect, useRef, useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Pencil } from "lucide-react";

/** Click-to-edit text field. Saves on blur or Enter, cancels on Escape. */
export function InlineEditText({
  value,
  onSave,
  canEdit,
  className,
  inputClassName,
  placeholder,
}: {
  value: string;
  onSave: (newValue: string) => Promise<void> | void;
  canEdit: boolean;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  async function commit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) { setEditing(false); setDraft(value); return; }
    setSaving(true);
    try { await onSave(trimmed); } finally { setSaving(false); setEditing(false); }
  }

  if (!canEdit) return <span className={className}>{value}</span>;

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        disabled={saving}
        placeholder={placeholder}
        className={inputClassName ?? "h-auto py-0.5 text-inherit font-inherit"}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`group cursor-pointer rounded-sm hover:bg-muted/50 ${className ?? ""}`}
      title="Click to edit"
    >
      {value}
      <Pencil className="ml-1 inline h-3 w-3 opacity-0 group-hover:opacity-40" />
    </span>
  );
}

/** Click-to-edit select field. Saves immediately on change. */
export function InlineEditSelect({
  value,
  displayValue,
  options,
  onSave,
  canEdit,
  className,
}: {
  value: string;
  displayValue?: string;
  options: { value: string; label: string }[];
  onSave: (newValue: string) => Promise<void> | void;
  canEdit: boolean;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => { if (editing) selectRef.current?.focus(); }, [editing]);

  async function handleChange(newVal: string) {
    if (newVal === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(newVal); } finally { setSaving(false); setEditing(false); }
  }

  if (!canEdit) return <span className={className}>{displayValue ?? value}</span>;

  if (editing) {
    return (
      <select
        ref={selectRef}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => setEditing(false)}
        disabled={saving}
        className="rounded border border-input bg-background px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`group cursor-pointer rounded-sm hover:bg-muted/50 ${className ?? ""}`}
      title="Click to change"
    >
      {displayValue ?? value}
      <Pencil className="ml-1 inline h-3 w-3 opacity-0 group-hover:opacity-40" />
    </span>
  );
}

/** Click-to-edit datetime field. Shows formatted text, opens datetime-local input on click. */
export function InlineEditTime({
  value,
  displayValue,
  onSave,
  canEdit,
  className,
  type,
  compact,
}: {
  value: string; // ISO string
  displayValue: ReactNode;
  onSave: (newIso: string) => Promise<void> | void;
  canEdit: boolean;
  className?: string;
  type?: "datetime-local" | "time" | "date";
  /** Position the edit pencil absolutely so it reserves no inline width --
   *  keeps tightly-packed values (e.g. a "start - end" time range) from
   *  being pushed apart by the (normally hidden) pencil affordance. */
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  function toInputValue(iso: string): string {
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60 * 1000);
    if (type === "date") return local.toISOString().slice(0, 10);
    if (type === "time") return local.toISOString().slice(11, 16);
    return local.toISOString().slice(0, 16);
  }

  async function handleChange(inputVal: string) {
    if (!inputVal) { setEditing(false); return; }
    let newIso: string;
    const original = new Date(value);
    if (type === "time") {
      // Keep original date, replace time
      const [h, m] = inputVal.split(":").map(Number);
      const updated = new Date(original);
      updated.setHours(h, m, 0, 0);
      newIso = updated.toISOString();
    } else if (type === "date") {
      // Keep original time, replace date
      const [y, mo, d] = inputVal.split("-").map(Number);
      const updated = new Date(original);
      updated.setFullYear(y, mo - 1, d);
      newIso = updated.toISOString();
    } else {
      newIso = new Date(inputVal).toISOString();
    }
    if (newIso === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(newIso); } finally { setSaving(false); setEditing(false); }
  }

  if (!canEdit) return <span className={className}>{displayValue}</span>;

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type ?? "datetime-local"}
        defaultValue={toInputValue(value)}
        onBlur={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); handleChange((e.target as HTMLInputElement).value); }
          if (e.key === "Escape") setEditing(false);
        }}
        disabled={saving}
        className="rounded border border-input bg-background px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`group cursor-pointer rounded-sm hover:bg-muted/50 ${compact ? "relative" : ""} ${className ?? ""}`}
      title="Click to change"
    >
      {displayValue}
      <Pencil
        className={`h-3 w-3 opacity-0 group-hover:opacity-40 ${
          compact ? "absolute left-full top-1/2 ml-0.5 -translate-y-1/2" : "ml-1 inline"
        }`}
      />
    </span>
  );
}
